import { Component, OnInit, OnDestroy, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { StaffService, Staff as StaffModel } from '../../../services/staff';
import { AdminHeader } from '../../../services/admin-header';

@Component({
	selector: 'app-staff',
	imports: [CommonModule, ReactiveFormsModule],
	templateUrl: './staff.html',
	styleUrl: './staff.css',
})
export class Staff implements OnInit, OnDestroy {
	private staffService = inject(StaffService);
	private fb = inject(FormBuilder);
	private adminHeader = inject(AdminHeader);

	staffList = signal<StaffModel[]>([]);
	isLoading = signal(true);
	showModal = signal(false);
	isEditMode = signal(false);
	isSubmitting = signal(false);
	errorMessage = signal('');
	selectedStaff = signal<StaffModel | null>(null);

	staffForm: FormGroup = this.fb.group({
		first_name: ['', Validators.required],
		last_name: ['', Validators.required],
		email: ['', Validators.email],
		phone: [''],
		role: ['stylist', Validators.required],
		working_hours_start: ['10:00'],
		working_hours_end: ['20:00'],
		is_active: [true],
	});

	ngOnInit() {
		this.loadStaff();
		this.adminHeader.setHeaderAction({
			label: 'Add Staff',
			icon: 'M12 6v6m0 0v6m0-6h6m-6 0H6',
			callback: () => this.openModal(),
		});
	}

	ngOnDestroy() {
		this.adminHeader.clearHeaderAction();
	}

	loadStaff() {
		this.isLoading.set(true);
		this.staffService.getAll().subscribe({
			next: (res) => {
				if (res.status) this.staffList.set(res.data);
				this.isLoading.set(false);
			},
			error: () => this.isLoading.set(false),
		});
	}

	openModal(staff?: StaffModel) {
		if (staff) {
			this.isEditMode.set(true);
			this.selectedStaff.set(staff);
			this.staffForm.patchValue(staff);
		} else {
			this.isEditMode.set(false);
			this.selectedStaff.set(null);
			this.staffForm.reset({ role: 'stylist', working_hours_start: '10:00', working_hours_end: '20:00', is_active: true });
		}
		this.showModal.set(true);
	}

	closeModal() {
		this.showModal.set(false);
		this.staffForm.reset();
		this.errorMessage.set('');
	}

	saveStaff() {
		if (this.staffForm.invalid) return;

		this.isSubmitting.set(true);
		const data = this.staffForm.value;

		const request = this.isEditMode()
			? this.staffService.update(this.selectedStaff()!.id, data)
			: this.staffService.create(data);

		request.subscribe({
			next: () => {
				this.loadStaff();
				this.closeModal();
				this.isSubmitting.set(false);
			},
			error: (err) => {
				this.errorMessage.set(err.error?.message || 'Failed to save staff');
				this.isSubmitting.set(false);
			},
		});
	}

	deleteStaff(staff: StaffModel) {
		if (!confirm(`Delete ${staff.full_name}?`)) return;

		this.staffService.delete(staff.id).subscribe({
			next: () => this.loadStaff(),
			error: (err) => alert(err.error?.message || 'Failed to delete'),
		});
	}
}
